import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { of, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  CurrentMoneyDaySummary,
  MoneyCashboxChangedEvent,
  MoneyFlowChangedEvent,
  MoneyDay,
  MoneyDayCollectionStatus,
  MoneyDayStatus
} from 'src/typings';
import { environment } from 'src/environments/environment';
import { normalizeMoneyDay } from './money-flow.utils';
import {
  BankStorageKindEnum,
  MoneyDayCollectionStatusEnum,
  MoneyDayStatusEnum,
  MoneyTypeEnum
} from 'src/enums';
import { toLocalDateString } from '../shared/utils/date-filter.util';
import { MoneyContainerService } from './money-container.service';

export interface SearchMoneyDayDto {
  id?: number;
  manager_user_id?: number;
  warehouse_id?: number;
  status?: MoneyDayStatus;
  collection_status?: MoneyDayCollectionStatus;
  business_date?: Date | string;
  date_from?: Date | string;
  date_to?: Date | string;
  limit?: number;
  offset?: number;
}

export interface CloseMoneyDayDto {
  close_comment?: string;
  lines?: Array<{
    money_type: MoneyTypeEnum;
    actual_amount?: number;
    actual_payment_amount?: number;
    actual_payment_count?: number | null;
    actual_refund_amount?: number;
    actual_refund_count?: number | null;
    comment?: string;
  }>;
}

export interface SubmitMoneyDayTerminalBatchDto {
  payment_terminal_id?: number | null;
  provider_batch_id?: string | null;
  reported_sale_amount: number;
  reported_sale_count: number;
  reported_refund_amount: number;
  reported_refund_count: number;
  reported_void_amount: number;
  reported_void_count: number;
  confirmed_no_refunds: boolean;
  comment?: string;
}

export interface SubmitMoneyDayReconciliationDto extends CloseMoneyDayDto {
  request_id: string;
  base_revision?: number;
  terminal_batches: SubmitMoneyDayTerminalBatchDto[];
}

export interface ReviewMoneyDayReconciliationDto {
  request_id: string;
  comment?: string;
}

export interface RejectMoneyDayReconciliationDto {
  request_id: string;
  reason: string;
}

@Injectable({
  providedIn: 'root'
})
export class MoneyDayService {
  readonly cashboxChanges =
    this.socket.fromEvent<MoneyCashboxChangedEvent>('money_cashbox_changed');

  readonly flowChanges = this.socket.fromEvent<MoneyFlowChangedEvent>('money_flow_changed');

  private readonly localFlowChangesSubject = new Subject<void>();

  readonly localFlowChanges = this.localFlowChangesSubject.asObservable();

  private readonly lifecycleChangesSubject = new Subject<MoneyDay>();

  readonly lifecycleChanges = this.lifecycleChangesSubject.asObservable();

  constructor(
    private http: HttpClient,
    private socket: Socket,
    private moneyContainerService: MoneyContainerService
  ) {}

  notifyMoneyFlowChanged() {
    this.localFlowChangesSubject.next();
  }

  resolveCurrentMoneyDay(managerUserId: number, warehouseId: number, businessDate?: Date | string) {
    const resolvedBusinessDate = toLocalDateString(businessDate || new Date()) || undefined;
    if (!managerUserId) {
      return of(
        this.toCurrentMoneyDaySummary(
          null,
          toLocalDateString(businessDate || new Date()) || businessDate || null
        )
      );
    }

    return this.search({
      manager_user_id: Number(managerUserId),
      status: MoneyDayStatusEnum.OPEN,
      limit: 2,
      offset: 0
    }).pipe(
      switchMap((response) => {
        const day = this.pickPreferredCurrentDay(response.items || []);
        if (day) {
          const summary = this.toCurrentMoneyDaySummary(day, resolvedBusinessDate);
          if (!summary.cashbox_id) {
            return this.resolveOperationalCashbox(
              managerUserId,
              Number(day.warehouse_id || warehouseId || 0)
            ).pipe(
              map((cashbox) =>
                cashbox
                  ? {
                      ...summary,
                      cashbox_id: Number(cashbox.id),
                      cashbox_name: cashbox.description || null,
                      cashbox_balance:
                        summary.cashbox_balance === undefined || summary.cashbox_balance === null
                          ? Number(cashbox.balance || 0)
                          : summary.cashbox_balance
                    }
                  : summary
              )
            );
          }
          if (summary.cashbox_name) return of(summary);

          return this.moneyContainerService.getOne(summary.cashbox_id).pipe(
            map((cashbox) => ({
              ...summary,
              cashbox_name: cashbox.description || null
            })),
            catchError(() => of(summary))
          );
        }

        if (!warehouseId) {
          return of(this.toCurrentMoneyDaySummary(null, resolvedBusinessDate));
        }

        return this.resolveOperationalCashbox(managerUserId, warehouseId).pipe(
          map((cashbox) => {
            const summary = this.toCurrentMoneyDaySummary(null, resolvedBusinessDate);
            if (!cashbox) return summary;

            const balance = Number(cashbox.balance || 0);
            return {
              ...summary,
              cashbox_id: Number(cashbox.id),
              cashbox_name: cashbox.description || null,
              cashbox_balance: balance,
              opening_cash_amount: balance,
              cash_expected: balance,
              cash_actual: balance,
              cash_summary: {
                opening_amount: balance,
                customer_payments: 0,
                customer_refunds: 0,
                correction_reversals: 0,
                correction_replacements: 0,
                adjustments: 0,
                expense_amount: 0,
                transfers: 0,
                other_movements: 0,
                movement_amount: 0,
                expected_amount: balance,
                actual_amount: balance,
                variance_amount: 0,
                ledger_balance: balance,
                current_ledger_balance: balance,
                boundary_at: new Date()
              }
            };
          })
        );
      })
    );
  }

  private resolveOperationalCashbox(managerUserId: number, warehouseId: number) {
    return this.moneyContainerService
      .search({
        storageKind: BankStorageKindEnum.MANAGER_CASHBOX,
        moneyType: MoneyTypeEnum.nal,
        owner_user_id: Number(managerUserId),
        warehouse_id: Number(warehouseId),
        isOperational: true,
        limit: 2,
        offset: 0
      })
      .pipe(
        map((containers) => {
          if (containers.total > 1 || containers.items.length > 1) {
            throw new Error('multiple_operational_cashboxes');
          }
          return containers.items[0] || null;
        })
      );
  }

  search(payload: SearchMoneyDayDto) {
    return this.http
      .post<{ items: MoneyDay[]; total: number }>(
        `${environment.BACKEND}/money-day/search`,
        payload
      )
      .pipe(
        map((response) => ({
          ...response,
          items: (response.items || []).map((item) => normalizeMoneyDay(item))
        }))
      );
  }

  getOne(id: number) {
    return this.http
      .get<MoneyDay>(`${environment.BACKEND}/money-day/${id}`)
      .pipe(map((day) => normalizeMoneyDay(day)));
  }

  close(id: number, payload: CloseMoneyDayDto) {
    return this.http.post<MoneyDay>(`${environment.BACKEND}/money-day/${id}/close`, payload).pipe(
      map((day) => normalizeMoneyDay(day)),
      tap((day) => this.lifecycleChangesSubject.next(day))
    );
  }

  submitReconciliation(id: number, payload: SubmitMoneyDayReconciliationDto) {
    return this.http
      .post<MoneyDay>(`${environment.BACKEND}/money-day/${id}/submit-reconciliation`, payload)
      .pipe(
        map((day) => normalizeMoneyDay(day)),
        tap((day) => this.lifecycleChangesSubject.next(day))
      );
  }

  verifyReconciliation(id: number, submissionId: number, payload: ReviewMoneyDayReconciliationDto) {
    return this.http
      .post<MoneyDay>(
        `${environment.BACKEND}/money-day/${id}/reconciliation/${submissionId}/verify`,
        payload
      )
      .pipe(
        map((day) => normalizeMoneyDay(day)),
        tap((day) => this.lifecycleChangesSubject.next(day))
      );
  }

  rejectReconciliation(id: number, submissionId: number, payload: RejectMoneyDayReconciliationDto) {
    return this.http
      .post<MoneyDay>(
        `${environment.BACKEND}/money-day/${id}/reconciliation/${submissionId}/reject`,
        payload
      )
      .pipe(
        map((day) => normalizeMoneyDay(day)),
        tap((day) => this.lifecycleChangesSubject.next(day))
      );
  }

  reopen(id: number, reason: string) {
    return this.http
      .post<MoneyDay>(`${environment.BACKEND}/money-day/${id}/reopen`, { reason })
      .pipe(
        map((day) => normalizeMoneyDay(day)),
        tap((day) => this.lifecycleChangesSubject.next(day))
      );
  }

  toCurrentMoneyDaySummary(
    day: MoneyDay | null,
    businessDate?: Date | string | null
  ): CurrentMoneyDaySummary {
    return this.mapCurrentMoneyDaySummary(day, businessDate);
  }

  private pickPreferredCurrentDay(days: MoneyDay[]) {
    if (!days?.length) return null;

    const sorted = [...days].sort((left, right) => Number(right.id || 0) - Number(left.id || 0));
    const activeDays = sorted.filter((day) => day.status === MoneyDayStatusEnum.OPEN);
    if (activeDays.length > 1) {
      throw new Error('multiple_active_money_days');
    }
    return activeDays[0] || null;
  }

  private mapCurrentMoneyDaySummary(
    day: MoneyDay | null,
    businessDate?: Date | string | null
  ): CurrentMoneyDaySummary {
    if (!day) {
      return {
        day_id: null,
        business_date: businessDate || null,
        status: null,
        collection_status: null,
        warehouse_id: null,
        warehouse_name: null,
        cash_expected: 0,
        cash_actual: 0,
        cash_variance: 0,
        cashbox_id: null,
        cashbox_name: null,
        cashbox_balance: 0,
        opening_cash_amount: 0,
        cash_summary: null,
        can_close: false,
        can_collect: false
      };
    }

    const cashLine =
      (day.lines || []).find((line) => line.money_type === MoneyTypeEnum.nal) ||
      (day.current_expected_lines || []).find((line) => line.money_type === MoneyTypeEnum.nal) ||
      null;

    return {
      day_id: day.id,
      business_date: day.business_date,
      status: day.status,
      collection_status: day.collection_status,
      warehouse_id: day.warehouse_id || null,
      warehouse_name: day.warehouse_name || null,
      cash_expected: Number(day.cash_summary?.expected_amount ?? cashLine?.expected_amount ?? 0),
      cash_actual: Number(day.cash_summary?.actual_amount ?? cashLine?.actual_amount ?? 0),
      cash_variance: Number(day.cash_summary?.variance_amount ?? cashLine?.variance_amount ?? 0),
      cashbox_id: day.cashbox_id || null,
      cashbox_name: day.cashbox_name || null,
      cashbox_balance: this.resolveCashboxBalance(day),
      opening_cash_amount: Number(
        day.opening_cash_amount ?? day.cash_summary?.opening_amount ?? day.cash_carry_amount ?? 0
      ),
      cash_summary: day.cash_summary || null,
      can_close: day.status === MoneyDayStatusEnum.OPEN,
      can_collect:
        Boolean(day.can_collect) &&
        day.status === MoneyDayStatusEnum.CLOSED &&
        day.collection_status === MoneyDayCollectionStatusEnum.PENDING &&
        (day.reconciliation_status === undefined ||
          day.reconciliation_status === null ||
          ['VERIFIED', 'LEGACY'].includes(String(day.reconciliation_status).toUpperCase()))
    };
  }

  private resolveCashboxBalance(day: MoneyDay) {
    const value =
      day.cash_summary?.current_ledger_balance ??
      day.cash_summary?.ledger_balance ??
      day.cashbox_balance;
    return value === undefined || value === null ? undefined : Number(value);
  }
}
